import React from "react";
import { Box, Typography, Paper, Card, CardContent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createRecipeStyles } from "./styles";
import { MethodIcon } from "../ui/RecipeIcons";
import {
  FetchedRecipe,
  MixingMethod,
  PouringMethod,
  HomogenizingMethod,
  DryingMethod,
} from "../../types/recipe";

/**
 * Props for RecipeMethod component.
 * @property {FetchedRecipe} recipe - The recipe object containing method steps.
 */
interface RecipeMethodProps {
  recipe: FetchedRecipe;
}

/**
 * RecipeMethod Component
 *
 * Renders the method steps for a recipe, including ambient conditions and
 * step-by-step cards for each method type (mixing, pouring, homogenizing, drying).
 * Uses Material UI for layout and styling, with styles centralized in styles.ts.
 *
 * Features:
 * - Shows ambient conditions if available
 * - Renders each method step in a card with details
 * - Handles multiple method types with custom details
 * - Responsive and scrollable layout
 *
 * @param {RecipeMethodProps} props - Component props
 * @returns {JSX.Element} The rendered method section
 */
const RecipeMethod: React.FC<RecipeMethodProps> = ({ recipe }) => {
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  /**
   * Renders details for each method type.
   * @param {any} method - The method object.
   * @param {string} type - The method type.
   * @returns {JSX.Element|null} The rendered details for the method.
   */
  const renderMethodDetails = (method: any, type: string) => {
    switch (type) {
      case "mixing":
        // Mixing method details
        return (
          <>
            {(method as MixingMethod).mixingDuration && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Mixing duration:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as MixingMethod).mixingDuration}{" "}
                    {(method as MixingMethod).mixingDurationUnit}
                  </Box>
                </Typography>
              </Box>
            )}
            {(method as MixingMethod).mixingStirringSpeed && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Stirring Speed:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as MixingMethod).mixingStirringSpeed}
                  </Box>
                </Typography>
              </Box>
            )}
            {(method as MixingMethod).mixingTemperature && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Temperature:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as MixingMethod).mixingTemperature}
                  </Box>
                </Typography>
              </Box>
            )}
          </>
        );
      case "pouring":
        // Pouring method details
        return (
          <>
            {(method as PouringMethod).pouringWeightBeforeDrying && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Weight Before Drying:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as PouringMethod).pouringWeightBeforeDrying}{" "}
                    {(method as PouringMethod).pouringWeightBeforeDryingUnit}
                  </Box>
                </Typography>
              </Box>
            )}
          </>
        );
      case "homogenizing":
        // Homogenizing method details
        return (
          <>
            {(method as HomogenizingMethod).homogenizingUltrasonicationTime && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Ultrasonication Time:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {
                      (method as HomogenizingMethod)
                        .homogenizingUltrasonicationTime
                    }{" "}
                    {
                      (method as HomogenizingMethod)
                        .homogenizingUltrasonicationTimeUnit
                    }
                  </Box>
                </Typography>
              </Box>
            )}
            {(method as HomogenizingMethod)
              .homogenizingUltrasonicationFrequency && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Ultrasonication Frequency:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {
                      (method as HomogenizingMethod)
                        .homogenizingUltrasonicationFrequency
                    }
                  </Box>
                </Typography>
              </Box>
            )}
          </>
        );
      case "drying":
        // Drying method details
        return (
          <>
            {(method as DryingMethod).dryingTime && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Drying time:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as DryingMethod).dryingTime}{" "}
                    {(method as DryingMethod).dryingTimeUnit}
                  </Box>
                </Typography>
              </Box>
            )}
            {(method as DryingMethod).dryingAmbientTemperature && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Ambient Temperature:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as DryingMethod).dryingAmbientTemperature}
                  </Box>
                </Typography>
              </Box>
            )}
            {(method as DryingMethod).dryingAmbientHumidity && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Ambient Humidity:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as DryingMethod).dryingAmbientHumidity}
                  </Box>
                </Typography>
              </Box>
            )}
            {(method as DryingMethod).dryingWeightAfterDrying && (
              <Box sx={styles.page.methodDetailRow}>
                <Typography variant="body1" sx={styles.page.methodDetailLabel}>
                  Weight After Drying:{" "}
                  <Box component="span" sx={styles.page.methodDetailValue}>
                    {(method as DryingMethod).dryingWeightAfterDrying}{" "}
                    {(method as DryingMethod).dryingWeightAfterDryingUnit}
                  </Box>
                </Typography>
              </Box>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} sx={styles.page.sectionCard}>
      {/* Section header */}
      <Box sx={styles.page.sectionHeader}>
        <MethodIcon />
        <Typography variant="h5" sx={styles.page.sectionTitle}>
          Method
        </Typography>
      </Box>
      {/* Main method content */}
      <Box sx={styles.page.methodMainContainer}>
        {/* Ambient conditions section */}
        <Box sx={styles.page.ambientConditionsSection}>
          <Typography variant="h6" sx={styles.page.ambientTitle}>
            Ambient conditions
          </Typography>
          {recipe.data.ambientConditions && (
            <Box>
              {/* Temperature */}
              <Box sx={styles.page.ambientRow}>
                <Typography variant="body1" sx={styles.page.ambientLabel}>
                  Ambient temperature
                </Typography>
                <Typography variant="body1">
                  {recipe.data.ambientConditions.temperature} Degrees celsius
                  (C)
                </Typography>
              </Box>
              {/* Humidity */}
              <Box sx={styles.page.ambientRow}>
                <Typography variant="body1" sx={styles.page.ambientLabel}>
                  Ambient humidity
                </Typography>
                <Typography variant="body1">
                  {recipe.data.ambientConditions.humidity} Relative Humidity
                  (RH)
                </Typography>
              </Box>
              {/* Light */}
              <Box sx={styles.page.ambientRow}>
                <Typography variant="body1" sx={styles.page.ambientLabel}>
                  Natural light conditions
                </Typography>
                <Typography variant="body1">
                  {recipe.data.ambientConditions.light}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {/* Method steps section */}
        <Box sx={styles.page.methodStepsSection}>
          {Array.isArray(recipe.data.methods) &&
          recipe.data.methods.length > 0 ? (
            recipe.data.methods.map((method, key) =>
              method ? (
                <Box key={key} sx={styles.page.methodStepBox}>
                  <Card variant="outlined" sx={styles.page.methodCard}>
                    <CardContent>
                      <Box sx={styles.page.methodStepHeader}>
                        <Box sx={styles.page.stepNumber}>{key + 1}</Box>
                        <Box sx={{ flex: 1, ml: 2 }}>
                          {/* Method type */}
                          <Box sx={styles.page.methodTypeRow}>
                            <Typography
                              variant="h6"
                              sx={styles.page.methodTitle}
                            >
                              Type:{" "}
                              <Box
                                component="span"
                                sx={styles.page.methodTypeValue}
                              >
                                {method.type || "N/A"}
                              </Box>
                            </Typography>
                          </Box>
                          {/* Method description */}
                          <Box sx={styles.page.methodDescriptionRow}>
                            <Typography
                              variant="body2"
                              sx={styles.page.methodDescriptionLabel}
                            >
                              Description:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {method.description || "No description provided."}
                            </Typography>
                          </Box>
                          {/* Render method-specific details */}
                          {renderMethodDetails(method, method.type || "")}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ) : null
            )
          ) : (
            <Typography variant="body2" color="text.secondary">
              No method steps provided.
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default RecipeMethod;
